import { isNativeApp } from "./platform";

const MEASUREMENT_ID = "G-XXKV4TB2TY";
const DISABLE_KEY = `ga-disable-${MEASUREMENT_ID}`;
const CAMPAIGN_VALUE = /^[a-zA-Z0-9][a-zA-Z0-9 _.\-]{0,99}$/;

type GoogleTagWindow = Window & {
  dataLayer?: IArguments[];
  gtag?: (...args: unknown[]) => void;
  [DISABLE_KEY]?: boolean;
};

let tagLoaded = false;

function landingAcquisition() {
  const campaign: Record<string, string> = {};
  const parameters = new URLSearchParams(window.location.search);
  for (const [queryKey, configKey] of [
    ["utm_source", "campaign_source"],
    ["utm_medium", "campaign_medium"],
    ["utm_campaign", "campaign_name"],
  ] as const) {
    const value = parameters.get(queryKey)?.trim();
    if (value && CAMPAIGN_VALUE.test(value)) campaign[configKey] = value;
  }

  let referrer = "";
  try {
    const url = new URL(document.referrer);
    if ((url.protocol === "https:" || url.protocol === "http:") && url.hostname !== window.location.hostname) {
      referrer = url.origin;
    }
  } catch { /* no usable external referrer */ }
  return { ...campaign, page_referrer: referrer };
}

/** Stop GA4 immediately when a visitor objects or enters a private surface. */
export function disableGoogleAnalytics() {
  if (typeof window === "undefined") return;
  (window as GoogleTagWindow)[DISABLE_KEY] = true;
}

/** Send only page views approved by the caller's current route and privacy checks. */
export function trackGooglePageView(path: string, allowed: boolean) {
  if (typeof window === "undefined" || isNativeApp() || !allowed) {
    disableGoogleAnalytics();
    return;
  }
  const pathname = path.split(/[?#]/, 1)[0] || "/";
  const pageLocation = `${window.location.origin}${pathname}`;

  const tagWindow = window as GoogleTagWindow;
  tagWindow[DISABLE_KEY] = false;
  const firstPageView = !tagLoaded;
  const acquisition = firstPageView ? landingAcquisition() : null;
  if (firstPageView) {
    tagLoaded = true;
    const dataLayer = tagWindow.dataLayer ?? [];
    tagWindow.dataLayer = dataLayer;
    tagWindow.gtag = function () { dataLayer.push(arguments); };
    tagWindow.gtag("js", new Date());
    // Route changes are reported below. The GA4 stream must also disable
    // Enhanced Measurement's history-change page views in its admin settings.
    tagWindow.gtag("config", MEASUREMENT_ID, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      page_location: pageLocation,
      ...(acquisition ?? {}),
    });
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    document.head.appendChild(script);
  } else tagWindow.gtag?.("set", { page_location: pageLocation, page_referrer: "" });
  tagWindow.gtag?.("event", "page_view", {
    send_to: MEASUREMENT_ID,
    page_location: pageLocation,
    ...(acquisition ?? { page_referrer: "" }),
  });
}
